import { useInfiniteQuery } from "@tanstack/react-query";
import { z } from "zod";
import { ActivityEntry, type DefinitionDetail } from "@workcore/schemas";
import { get } from "../../api";
import { useUsers } from "../../directory";
import { ErrorBox, Panel, when, When } from "../../ui";
import { defKey } from "./shared";

const KIND: Record<string, string> = { definition: "workflow", object_type: "type", state: "state", field: "field", work_object: "object" };

function show(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v.length > 60 ? `${v.slice(0, 60)}…` : v;
  return JSON.stringify(v);
}

/** Keys that only restate where the change happened. */
const NOISE = new Set(["definition_id", "type_id"]);

function Changes({ changes, def }: { changes: Record<string, unknown>; def: DefinitionDetail }) {
  const typeName = (id: string) => (id === "root" ? "project root" : def.types.find((t) => String(t.id) === id)?.name ?? `type ${id}`);
  const value = (k: string, v: unknown) => {
    // containment rules are recorded as "<parentId|root>><childId>"
    if (k === "rule" && typeof v === "string" && v.includes(">")) {
      const [p, c] = v.split(">");
      return `${typeName(p ?? "")} › ${typeName(c ?? "")}`;
    }
    return show(v);
  };
  const parts = Object.entries(changes)
    .filter(([k]) => !NOISE.has(k))
    .map(([k, c]) => {
      const x = c as { old?: unknown; new?: unknown; added?: unknown[]; removed?: unknown[] };
      if (x.added || x.removed) return `${k}: +${(x.added ?? []).length} −${(x.removed ?? []).length}`;
      if (x.old === null || x.old === undefined) return `${k} = ${value(k, x.new)}`;
      if (x.new === null || x.new === undefined) return `${k} ${value(k, x.old)} removed`;
      return `${k}: ${value(k, x.old)} → ${value(k, x.new)}`;
    });
  return <span className="muted small">{parts.join(" · ")}</span>;
}

export function HistoryTab({ slug, def }: { slug: string; def: DefinitionDetail }) {
  const users = useUsers(slug).data ?? [];
  const name = (e: ActivityEntry) => (e.actorKind === "system" ? "system" : e.actorKind === "user" ? users.find((u) => u.id === e.actorId)?.displayName ?? `user ${e.actorId}` : `${e.actorKind} ${e.actorId}`);
  const thing = (e: ActivityEntry) => {
    if (e.entityKind === "object_type") return def.types.find((t) => t.id === e.entityId)?.name ?? `type #${e.entityId}`;
    if (e.entityKind === "state") for (const t of def.types) { const s = t.states.find((x) => x.id === e.entityId); if (s) return `${t.name} › ${s.name}`; }
    if (e.entityKind === "field") return def.fields.find((f) => f.id === e.entityId)?.label ?? `field #${e.entityId}`;
    if (e.entityKind === "definition") return def.name;
    return `#${e.entityId}`;
  };
  const q = useInfiniteQuery({
    queryKey: [...defKey(slug, def.id), "history"],
    initialPageParam: undefined as number | undefined,
    queryFn: async ({ pageParam }) => z.array(ActivityEntry).parse(await get(`/v1/ws/${slug}/definitions/${def.id}/history`, { limit: 50, before: pageParam })),
    getNextPageParam: (last) => (last.length === 50 ? last.at(-1)?.id : undefined),
  });
  const entries = q.data?.pages.flat() ?? [];
  return (
    <Panel title="History">
      <ErrorBox error={q.error} />
      <ol className="timeline">
        {entries.map((e) => (
          <li key={e.id}>
            <When at={e.at} /> <strong>{name(e)}</strong> {e.action.replace(/_/g, " ")} <span className="tag">{KIND[e.entityKind] ?? e.entityKind}</span> {thing(e)} <Changes changes={e.changes} def={def} />
          </li>
        ))}
      </ol>
      {entries.length === 0 && !q.isLoading && <p className="muted">Nothing recorded yet.</p>}
      {q.hasNextPage && <button className="secondary" onClick={() => void q.fetchNextPage()}>Older</button>}
    </Panel>
  );
}
